import React, { useState, useEffect, useMemo, useRef } from 'react';
import { CALENDAR } from '../data/config';

// ── CALENDAR SECTION ──────────────────────────────────────────
// Fetches events from every calendar in CALENDAR.calendars (src/data/config.js)
// via the Google Calendar API, merges them, and renders filter chips so
// visitors can view all events or just one committee's.
//
// To add/edit events: use Google Calendar directly (shpe581@gmail.com).
// Changes appear here automatically, no code change needed — this just
// reads events, it never writes to the calendar.
//
// OWNER ACCESS: only people with edit access on a given Google Calendar
// can add/edit/delete its events. This view is read-only for all visitors.

const MAX_EVENTS_PER_CALENDAR = 20;

function formatEventTime(event) {
  if (!event.start) return 'Date TBD';
  const date = new Date(event.start);
  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
  const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  if (event.allDay) return `${weekday}, ${dateStr}`;
  const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${weekday}, ${dateStr} · ${timeStr}`;
}

function formatDateBadge(event) {
  if (!event.start) return { month: '—', day: '?' };
  const date = new Date(event.start);
  return {
    month: date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
    day: date.getDate(),
  };
}

export default function CalendarSection({ currentUser, isAdmin }) {
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [activeIds, setActiveIds] = useState(() => new Set(CALENDAR.calendars.map(c => c.id)));
  const [filterOpen, setFilterOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const filterRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    if (!CALENDAR.apiKey || CALENDAR.calendars.length === 0) {
      setStatus('error');
      return;
    }

    const timeMin = new Date().toISOString();

    Promise.all(
      CALENDAR.calendars.map(cal =>
        fetch(
          `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(cal.id)}/events` +
          `?key=${CALENDAR.apiKey}&singleEvents=true&orderBy=startTime` +
          `&timeMin=${encodeURIComponent(timeMin)}&maxResults=${MAX_EVENTS_PER_CALENDAR}`
        )
          .then(res => {
            if (!res.ok) throw new Error(`Calendar fetch failed for ${cal.name}`);
            return res.json();
          })
          .then(data =>
            (data.items || []).map(item => ({
              id: `${cal.id}-${item.id}`,
              title: item.summary || '(Untitled event)',
              start: item.start?.dateTime || item.start?.date,
              allDay: !item.start?.dateTime,
              location: item.location || '',
              htmlLink: item.htmlLink,
              calendarId: cal.id,
              calendarName: cal.name,
              calendarColor: cal.color,
            }))
          )
          .catch(() => []) // one calendar failing (e.g. not public yet) shouldn't break the rest
      )
    ).then(results => {
      const merged = results.flat().sort((a, b) => new Date(a.start) - new Date(b.start));
      setEvents(merged);
      setStatus('ready');
    });
  }, []);

  const toggleCalendar = (id) => {
    setActiveIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const showAll = () => setActiveIds(new Set(CALENDAR.calendars.map(c => c.id)));

  const visibleEvents = useMemo(
    () => events.filter(e => activeIds.has(e.calendarId)),
    [events, activeIds]
  );

  // Collapse back to the short view whenever the filter changes, so switching
  // committees doesn't leave a tall expanded list sitting open.
  useEffect(() => {
    setExpanded(false);
  }, [activeIds]);

  const COLLAPSED_COUNT = 6;
  const shownEvents = expanded ? visibleEvents : visibleEvents.slice(0, COLLAPSED_COUNT);
  const hiddenCount = visibleEvents.length - shownEvents.length;

  const allSelected = activeIds.size === CALENDAR.calendars.length;

  return (
    <section id="calendar" style={{ padding: '80px 24px', background: 'white' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 32, flexWrap: 'wrap', gap: 20 }}>
          <div>
            <span className="section-label">Events Calendar</span>
            <h2 className="section-title">Stay in the Loop</h2>
            <p style={{ color: '#666', marginTop: 8, fontSize: 15, maxWidth: 480 }}>
              All SHPE@VT events are managed by chapter leadership. The calendar below is the official schedule — check it regularly!
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-end' }}>
            {/* Admin-only: link to manage calendars */}
            {isAdmin && (
              <a
                href="https://calendar.google.com/calendar/r"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
                style={{ fontSize: 13 }}>
                ✏️ Edit Calendars (Admin)
              </a>
            )}
          </div>
        </div>

        {/* ── FILTER DROPDOWN ──────────────────────────────────
            One compact button instead of a full row of chips. Click it to
            reveal a checklist of committees; selections apply immediately
            without closing, so multiple can be toggled in one visit. */}
        {CALENDAR.calendars.length > 1 && (
          <div ref={filterRef} style={{ position: 'relative', display: 'inline-block', marginBottom: 24 }}>
            <button
              onClick={() => setFilterOpen(o => !o)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '9px 16px', borderRadius: 10, fontSize: 13, fontWeight: 700,
                border: '1.5px solid #DDD', background: 'white', color: '#333',
                cursor: 'pointer',
              }}
            >
              {/* Up to 3 color dots preview so you can tell something's filtered at a glance */}
              {!allSelected && (
                <span style={{ display: 'flex', marginRight: 2 }}>
                  {CALENDAR.calendars.filter(c => activeIds.has(c.id)).slice(0, 3).map((c, i) => (
                    <span key={c.id} style={{
                      width: 9, height: 9, borderRadius: '50%', background: c.color,
                      border: '1.5px solid white', marginLeft: i === 0 ? 0 : -4,
                    }} />
                  ))}
                </span>
              )}
              {allSelected ? 'All Events' : `${activeIds.size} of ${CALENDAR.calendars.length} selected`}
              <span style={{ fontSize: 10, color: '#999', transform: filterOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }}>▾</span>
            </button>

            {filterOpen && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 20,
                minWidth: 220, background: 'white', borderRadius: 12,
                boxShadow: '0 12px 32px rgba(0,0,0,0.15)', border: '1px solid #EEE',
                padding: 6,
              }}>
                <button
                  onClick={showAll}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                    padding: '9px 12px', borderRadius: 8, border: 'none',
                    background: allSelected ? '#F5F0F0' : 'white', cursor: 'pointer',
                    fontSize: 13, fontWeight: 700, color: '#1A1A1A', textAlign: 'left',
                  }}
                >
                  <span style={{
                    width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                    border: `1.5px solid ${allSelected ? '#1A1A1A' : '#CCC'}`,
                    background: allSelected ? '#1A1A1A' : 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontSize: 11,
                  }}>{allSelected ? '✓' : ''}</span>
                  All Events
                </button>

                <div style={{ height: 1, background: '#F0F0F0', margin: '6px 4px' }} />

                {CALENDAR.calendars.map(cal => {
                  const selected = activeIds.has(cal.id);
                  return (
                    <button
                      key={cal.id}
                      onClick={() => toggleCalendar(cal.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                        padding: '9px 12px', borderRadius: 8, border: 'none',
                        background: 'white', cursor: 'pointer',
                        fontSize: 13, fontWeight: 600, color: '#333', textAlign: 'left',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#FAFAFA')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'white')}
                    >
                      <span style={{
                        width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                        border: `1.5px solid ${selected ? cal.color : '#CCC'}`,
                        background: selected ? cal.color : 'white',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'white', fontSize: 11,
                      }}>{selected ? '✓' : ''}</span>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: cal.color, flexShrink: 0 }} />
                      {cal.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── EVENT LIST ───────────────────────────────────── */}
        <div style={{
          borderRadius: 16, overflow: 'hidden',
          boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
          border: '1px solid #EEE',
          minHeight: 300,
        }}>
          {status === 'loading' && (
            <div style={{
              height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#999', fontSize: 14,
            }}>
              Loading events…
            </div>
          )}

          {status === 'error' && (
            <div style={{
              height: 300, background: 'linear-gradient(135deg, #F5F0F0 0%, #EDE5E8 100%)',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              textAlign: 'center', padding: 40,
            }}>
              <div style={{ fontSize: 64, marginBottom: 20 }}>📅</div>
              <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: 24, color: '#861F41', marginBottom: 12 }}>
                Calendar Coming Soon
              </h3>
              <p style={{ color: '#888', fontSize: 13, maxWidth: 340 }}>
                Add a Google Calendar API key and at least one calendar in src/data/config.js → CALENDAR.
              </p>
            </div>
          )}

          {status === 'ready' && visibleEvents.length === 0 && (
            <div style={{
              height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#999', fontSize: 14,
            }}>
              No upcoming events{!allSelected ? ' for this filter' : ''}.
            </div>
          )}

          {status === 'ready' && visibleEvents.length > 0 && (
            <>
              {/* Scrolls internally past this height, so an expanded list can
                  never push the rest of the page down indefinitely. */}
              <div style={{
                padding: '12px 8px',
                maxHeight: expanded ? 560 : 'none',
                overflowY: expanded ? 'auto' : 'visible',
              }}>
                {shownEvents.map((event, i) => {
                  const badge = formatDateBadge(event);
                  return (
                    <div
                      key={event.id}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: 20,
                        padding: '22px 24px',
                        borderRadius: 12,
                        borderBottom: i < shownEvents.length - 1 ? '1px solid #F2EEEF' : 'none',
                      }}
                    >
                      {/* Date badge */}
                      <div style={{
                        width: 56, flexShrink: 0, textAlign: 'center',
                        background: `${event.calendarColor}14`, borderRadius: 10,
                        padding: '8px 0',
                      }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: event.calendarColor, letterSpacing: 0.5 }}>
                          {badge.month}
                        </div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: '#1A1A1A', lineHeight: 1.1, marginTop: 2 }}>
                          {badge.day}
                        </div>
                      </div>

                      <div style={{ flex: 1, minWidth: 0, paddingTop: 2 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
                          <span style={{ fontWeight: 700, fontSize: 16, color: '#1A1A1A' }}>{event.title}</span>
                          <span style={{
                            fontSize: 11, fontWeight: 700, color: event.calendarColor,
                            background: `${event.calendarColor}1A`, padding: '3px 10px',
                            borderRadius: 100, whiteSpace: 'nowrap',
                          }}>
                            {event.calendarName}
                          </span>
                        </div>
                        <div style={{ fontSize: 13, color: '#888', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 8px' }}>
                          <span>🕐 {formatEventTime(event)}</span>
                          {event.location && <span>📍 {event.location}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {(hiddenCount > 0 || expanded) && (
                <button
                  onClick={() => setExpanded(e => !e)}
                  style={{
                    width: '100%', padding: '14px', border: 'none',
                    borderTop: '1px solid #F2EEEF', background: '#FAFAFA',
                    color: '#861F41', fontWeight: 700, fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  {expanded ? '↑ Show less' : `Show ${hiddenCount} more event${hiddenCount === 1 ? '' : 's'} ↓`}
                </button>
              )}
            </>
          )}
        </div>

      </div>
    </section>
  );
}